import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal'
import { useShowLocationModal } from 'context/general/ShowLocationModal';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import eventsApi from 'api/requests/events';
import colors from 'theme/colors';


export default function ModalLocation({ onChangeModalLocation = () => false }) {
    const { currentLocation, setCurrentLocation } = useCurrentLocation();
    const { showLocationModal, setShowLocationModal } = useShowLocationModal();
    const [, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false)
    const [locations, setLocations] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const searchInputRef = useRef(null)

    useEffect(() => {
        getActiveLocations();
    }, []);

    useEffect(() => {
        if (showLocationModal && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 300);
        }
        if (!showLocationModal) {
            setSearchTerm('');
        }
    }, [showLocationModal]);

    async function getActiveLocations() {
        setLoading(true);
        try {
            const { data: res } = await eventsApi.getActiveLocations();
            setLocations(res?.states || []);
        } catch (error) {
            console.log('error', error);
        } finally {
            setLoading(false);
        }
    }

    const filteredLocations = useMemo(() => {
        if (!searchTerm.trim()) return locations;

        const term = searchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        return locations
            .map((state) => {
                const stateName = state.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const stateMatches = stateName.includes(term);

                const filteredCities = state.cities?.filter((city) => {
                    const cityName = city.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    return cityName.includes(term);
                });

                if (stateMatches) return state;
                if (filteredCities?.length > 0) return { ...state, cities: filteredCities };
                return null;
            })
            .filter(Boolean);
    }, [searchTerm, locations]);

    function onChangeSelect(cityName, stateCode) {
        setCurrentLocation({ city: cityName, state: stateCode });
        setShowLocationModal(false);
        onChangeModalLocation({ city: cityName, state: stateCode });
        setSearchParams(new URLSearchParams());
    }

    function handleClose() {
        setShowLocationModal(false);
    }

    return (
        <Modal
            isOpen={showLocationModal}
            onClose={handleClose}
            childrenOverflowY='scroll'
            closeButton={false}
        >
            <ModalWrapper>
                <ModalHeader>
                    <CloseButton onClick={handleClose} aria-label="Fechar">
                        <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                            <path d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z" fill="currentColor" />
                        </svg>
                    </CloseButton>

                    <HeaderTitle>Escolha sua cidade</HeaderTitle>

                    <SearchWrapper>
                        <SearchSvg />
                        <SearchInput
                            ref={searchInputRef}
                            type="text"
                            placeholder="Buscar cidade ou estado..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Buscar cidade ou estado"
                        />
                        {searchTerm && (
                            <ClearButton
                                onClick={() => setSearchTerm('')}
                                aria-label="Limpar busca"
                            >
                                <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                                    <path d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z" fill="currentColor" />
                                </svg>
                            </ClearButton>
                        )}
                    </SearchWrapper>
                </ModalHeader>

                <ContentLocation>
                    {!searchTerm && (
                        <TodasCidadesRow
                            onClick={() => {
                                setCurrentLocation(null);
                                setShowLocationModal(false);
                                const params = new URLSearchParams(window.location.search);
                                if (params.has('city')) {
                                    params.delete('city');
                                    const newSearch = params.toString();
                                    navigate(window.location.pathname + (newSearch ? `?${newSearch}` : ''));
                                }
                            }}
                        >
                            🌍 Todas as cidades
                        </TodasCidadesRow>
                    )}
                    {loading ? (
                        <LoaderWrapper>
                            <Loader size="medium" />
                        </LoaderWrapper>
                    ) : filteredLocations.length === 0 ? (
                        <EmptyState>
                            <EmptyText>Nenhum resultado para "{searchTerm}"</EmptyText>
                        </EmptyState>
                    ) : (
                        filteredLocations.map((state, stateIndex) => (
                            <StateGroup key={state.code} $index={stateIndex}>
                                <StateName>{state.name}</StateName>

                                <CitiesGrid>
                                    {state.cities?.map((city) => {
                                        const isSelected = currentLocation?.city === city.name;
                                        return (
                                            <CityChip
                                                key={`${state.code}-${city.name}`}
                                                $selected={isSelected}
                                                onClick={() => onChangeSelect(city.name, state.code)}
                                                role="option"
                                                aria-selected={isSelected}
                                            >
                                                {city.name}
                                            </CityChip>
                                        );
                                    })}
                                </CitiesGrid>
                            </StateGroup>
                        ))
                    )}
                </ContentLocation>
            </ModalWrapper>
        </Modal>
    )
}

function SearchSvg() {
    return (
        <SearchIconWrapper>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M14.2939 12.5786H13.3905L13.0703 12.2699C14.191 10.9663 14.8656 9.27387 14.8656 7.43282C14.8656 3.32762 11.538 0 7.43282 0C3.32762 0 0 3.32762 0 7.43282C0 11.538 3.32762 14.8656 7.43282 14.8656C9.27387 14.8656 10.9663 14.191 12.2699 13.0703L12.5786 13.3905V14.2939L18.2962 20L20 18.2962L14.2939 12.5786ZM7.43282 12.5786C4.58548 12.5786 2.28702 10.2802 2.28702 7.43282C2.28702 4.58548 4.58548 2.28702 7.43282 2.28702C10.2802 2.28702 12.5786 4.58548 12.5786 7.43282C12.5786 10.2802 10.2802 12.5786 7.43282 12.5786Z" fill="currentColor" />
            </svg>
        </SearchIconWrapper>
    );
}


/* ============ Animations ============ */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

/* ============ Styled Components ============ */

const ModalWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

const ModalHeader = styled.div`
    position: relative;
    padding: 4px 0 16px;
    text-align: center;
    flex-shrink: 0;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: ${colors.night};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background: ${colors.neutralDark};
        color: ${colors.nightDark};
    }
`;

const HeaderTitle = styled.h2`
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 16px;
`;

const SearchWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const SearchIconWrapper = styled.div`
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.nightLight};
    display: flex;
    align-items: center;
    pointer-events: none;
    transition: color 0.2s ease;
`;

const SearchInput = styled.input`
    width: 100%;
    height: 44px;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 24px;
    padding: 0 40px 0 40px;
    font-size: 14px;
    color: ${colors.nightDark};
    background: ${colors.neutralBackground};
    outline: none;
    transition: all 0.2s ease;
    font-family: inherit;

    &::placeholder {
        color: ${colors.nightLight};
    }

    &:focus {
        border-color: ${colors.occasionalPurple};
        background: ${colors.neutralLight};
        box-shadow: 0 0 0 3px rgba(159, 22, 219, 0.08);
    }
`;

const ClearButton = styled.button`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: none;
    background: ${colors.neutralDark};
    color: ${colors.night};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.occasionalPurple};
        color: ${colors.neutralLight};
    }
`;

const ContentLocation = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    padding: 4px 0;
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
`;

const StateGroup = styled.div`
    width: 100%;
    animation: ${fadeIn} 0.3s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 0.05, 0.25)}s;

    & + & {
        margin-top: 20px;
    }
`;

const StateName = styled.span`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
`;

const CitiesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const CityChip = styled.button`
    padding: 8px 16px;
    border-radius: 20px;
    border: 1.5px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    font-size: 14px;
    font-weight: 500;
    color: ${colors.nightDark};
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
    white-space: nowrap;

    &:hover {
        border-color: ${colors.occasionalPurple};
        color: ${colors.occasionalPurple};
        background: rgba(159, 22, 219, 0.04);
    }

    ${({ $selected }) =>
        $selected &&
        css`
            border-color: ${colors.occasionalPurple};
            background: ${colors.occasionalPurple};
            color: ${colors.neutralLight};

            &:hover {
                background: ${colors.secondaryPurple};
                border-color: ${colors.secondaryPurple};
                color: ${colors.neutralLight};
            }
        `}
`;

const TodasCidadesRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    border-bottom: 1px solid ${colors.neutralDark};
    margin-bottom: 8px;
    transition: background 0.15s ease;
    &:hover { background: ${colors.neutralBackground}; }
`;

const EmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 16px;
`;

const EmptyText = styled.p`
    font-size: 14px;
    color: ${colors.nightLight};
    margin: 0;
`;
